/**
 * Redesigned Admin Dashboard - Active Users vs Invited Users
 */

import React, { useCallback, useEffect, useState } from 'react';
import { customerApi, User } from '../services/customerApi';
import type { ProfileData } from '../services/user.service';
import type { WaitlistProfileData } from '../services/customerApi';
import { UserRole } from '../services/user.service'; 
import { ApiClientError } from '../services/api';
import { clearAuthData } from '../utils/tokenValidation';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/logo.gif';



// Whitelist entry type (matches backend)
interface WhitelistEntry {
  _id: string;        // Required - MongoDB document ID
  email: string;
  role: string;
  addedBy: string;
  addedAt: string;
  invitationSent: boolean;
  userId?: string;    // Optional - only set after user logs in
}

// Modal state type
interface ModalState {
  open: boolean;
  mode: 'add' | 'edit-user' | 'edit-invite';
  entry: Partial<WhitelistEntry> | Partial<User> | null;
}

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'creator', label: 'Creator' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'waitlisted', label: 'Waitlisted' }
];


export default function AdminDashboard() {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [whitelist, setWhitelist] = useState<WhitelistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState<ModalState>({ open: false, mode: 'add', entry: null });
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WhitelistEntry | null>(null);
  
  // Filter states
  const [userRoleFilter, setUserRoleFilter] = useState<string>('all');
  const [inviteRoleFilter, setInviteRoleFilter] = useState<string>('all');
  const [waitlistProfilesByEmail, setWaitlistProfilesByEmail] = useState<Record<string, WaitlistProfileData | null>>({});
  const [directProfiles, setDirectProfiles] = useState<ProfileData[]>([]);

  /**
   * RESTORED: Error boundary component for better error handling
   */
  function ErrorBoundary({ children }: { children: React.ReactNode }) {
    const [hasError, setHasError] = useState(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
      const handleError = (error: ErrorEvent) => {
        setHasError(true);
        setError(error.message);
      };

      window.addEventListener('error', handleError);
      return () => window.removeEventListener('error', handleError);
    }, []);

    if (hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#161513]">
          <div className="text-center bg-[#161513] p-8 rounded-xl shadow-lg border border-[#0CB4E4]/30">
            <h2 className="text-xl font-semibold text-red-400 mb-2">Something went wrong</h2>
            <p className="text-gray-300 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-[#0C5484] text-white px-4 py-2 rounded-lg hover:bg-[#0CB4E4]/80 transition-colors shadow-md"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return <>{children}</>;
  }

  /**
   * Fetch data with centralized API client
   */
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [usersResponse, whitelistResponse, profilesResponse] = await Promise.all([
        customerApi.getAllUsers(),
        customerApi.getAllWhitelistEntries(),
        customerApi.getAllProfiles().catch(() => ({ success: false, data: [] as ProfileData[] }))
      ]);

      const usersData = Array.isArray(usersResponse)
        ? usersResponse
        : (usersResponse as any)?.data && Array.isArray((usersResponse as any).data)
          ? (usersResponse as any).data
          : [];

      const whitelistData = Array.isArray(whitelistResponse)
        ? whitelistResponse
        : (whitelistResponse as any)?.data && Array.isArray((whitelistResponse as any).data)
          ? (whitelistResponse as any).data
          : [];

      console.log('Users data:', usersData);
      console.log('Whitelist data:', whitelistData);
      if ((profilesResponse as any)?.success) {
        console.log('Profiles data:', (profilesResponse as any).data);
      }

      setUsers(usersData);
      setWhitelist(whitelistData);
      if ((profilesResponse as any)?.success && Array.isArray((profilesResponse as any).data)) {
        setDirectProfiles((profilesResponse as any).data);
      } else {
        setDirectProfiles([]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error instanceof ApiClientError) {
        if (error.status === 401) {
          clearAuthData?.();
          window.location.href = '/login';
          return;
        }
        setError(`API Error: ${error.message}`);
      } else {
        setError('Failed to load data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => { if (mounted) await fetchData(); })();
    return () => { mounted = false; };
  }, [fetchData]);

  // Fetch waitlist signup details for pending invitations (frontend-only join by email)
  useEffect(() => {
    const emails = Array.from(
      new Set(
        (whitelist || [])
          .filter((entry) => !entry.userId && entry.email)
          .map((entry) => entry.email)
      )
    );

    const emailsToFetch = emails.filter((email) => !(email in waitlistProfilesByEmail));
    if (emailsToFetch.length === 0) return;

    let cancelled = false;
    (async () => {
      const results: Record<string, WaitlistProfileData | null> = {};
      await Promise.allSettled(
        emailsToFetch.map(async (email) => {
          try {
            const resp = await customerApi.getWaitlistProfile(email);
            results[email] = resp.data as WaitlistProfileData;
          } catch (_err) {
            // If no profile exists or request fails, store null to avoid refetch loops
            console.info(
              `[AdminDashboard] No waitlist profile for ${email}. Using placeholder values in the Invited Users table.`
            );
            console.debug('[AdminDashboard] Waitlist lookup error details:', _err);
            results[email] = null;
          }
        })
      );
      if (!cancelled) {
        setWaitlistProfilesByEmail((prev) => ({ ...prev, ...results }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [whitelist, waitlistProfilesByEmail]);

  // Filter functions
  const filteredUsers = users.filter(user => 
    userRoleFilter === 'all' || user.role === userRoleFilter
  );

  // Only show invitations (entries without userId)
  const invitedUsers = whitelist
    .filter(entry => !entry.userId) // Only invitations
    .filter(entry => inviteRoleFilter === 'all' || entry.role === inviteRoleFilter);

  // Handlers for modal open/close
  const openAddInviteModal = () => setModal({ 
    open: true, 
    mode: 'add', 
    entry: { email: '', role: 'creator' } 
  });
  
  const openEditUserModal = (user: User) => setModal({ 
    open: true, 
    mode: 'edit-user', 
    entry: user 
  });
  
  const openEditInviteModal = (entry: WhitelistEntry) => setModal({ 
    open: true, 
    mode: 'edit-invite', 
    entry 
  });
  
  const closeModal = () => setModal({ open: false, mode: 'add', entry: null });

  // Helper function to format date
  const formatDate = (date: string | number | Date | undefined) => {
    if (!date) return 'Unknown';
    try {
      return new Date(date).toLocaleDateString();
    } catch (_e) {
      return 'Invalid Date';
    }
  };

  /**
   * Handle modal form submission
   */
  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modal.entry) return;

    try {
      setSubmitting(true);
      
      if (modal.mode === 'edit-user') {
        // Update active user role
        const user = modal.entry as User;
        await customerApi.updateUserRole(user.userId || user._id!, user.role!);
      } else if (modal.mode === 'edit-invite') {
        // Update invitation role
        const invite = modal.entry as WhitelistEntry;
        await customerApi.updateInvitationRole(invite.email!, invite.role! as UserRole);
      } else {
        // Add new invitation
        const invite = modal.entry as WhitelistEntry;
        await customerApi.addToWhitelist({
          email: invite.email!,
          role: invite.role!,
          addedBy: user?.email || user?.uid || 'admin',
          invitationSent: false
        });
      }
      
      closeModal();
      await fetchData(); // Refresh data
      
    } catch (error) {
      console.error('Failed to save entry:', error);
      
      if (error instanceof ApiClientError) {
        setError(`Save failed: ${error.message}`);
      } else {
        setError(`Failed to save entry: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle delete invitation (only for invites, not active users)
   */
  const handleDeleteInvite = async () => {
    if (!deleteTarget?.email) return;

    // Safety check - only allow deletion of invitations
    if (deleteTarget.userId) {
      setError('Cannot delete active users. This user has already logged in.');
      setDeleteTarget(null);
      return;
    }

    try {
      setSubmitting(true);
      
      // Pass email instead of _id for invitation deletion
      await customerApi.removeFromWhitelist(deleteTarget.email);
      setDeleteTarget(null);
      await fetchData(); // Refresh data
      
    } catch (error) {
      console.error('Failed to delete invitation:', error);
      
      if (error instanceof ApiClientError) {
        setError(`Delete failed: ${error.message}`);
      } else {
        setError(`Failed to delete invitation: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalInput = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setModal(prev => ({
      ...prev,
      entry: prev.entry ? { ...prev.entry, [name]: value } : { [name]: value }
    }));
  };

  return (
    <ErrorBoundary>
      <ProtectedRoute requiredRoles={['admin']}>
      <div className="min-h-screen bg-[#161513] px-4 py-8 relative">
        {/* Logo Background */}
        <div 
          className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none"
          style={{ backgroundImage: `url(${logo})` }}
        ></div>
      
        <div className="max-w-7xl mx-auto relative z-10">
          <h1 className="text-3xl font-bold text-white mb-8">Admin Dashboard</h1>

          {/* RESTORED: Global error banners with dismiss functionality */}
          {error && (
            <div className="bg-red-900/20 text-red-400 p-4 rounded-lg mb-4 font-medium border border-red-500/30">
              {error}
              <button 
                onClick={() => setError('')} 
                className="ml-4 text-red-300 hover:text-red-200"
              >
                ✕
              </button>
            </div>
          )}

          {/* RESTORED: Global empty database banner */}
          {!loading && users.length === 0 && whitelist.length === 0 && !error && (
            <div className="bg-[#0CB4E4]/10 text-[#0CB4E4] p-4 rounded-lg mb-6 font-medium text-center border border-[#0CB4E4]/30">
              Database is empty. No users or whitelisted users found.
            </div>
          )}

          {/* Active Users Table */}
          <div className="bg-[#161513] rounded-xl shadow-lg p-6 mb-8 border border-[#0CB4E4]/30">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Active Users</h2>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-300">Filter by role:</label>
                <select
                  value={userRoleFilter}
                  onChange={(e) => setUserRoleFilter(e.target.value)}
                  className="px-3 py-1 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] bg-[#161513] border border-[#0CB4E4]/30 text-white"
                >
                  <option value="all">All Roles</option>
                  {ROLE_OPTIONS.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {loading ? (
              <div className="py-6 text-center text-gray-300">Loading...</div>
            ) : error ? null : filteredUsers.length === 0 ? (
              <div className="py-6 text-center text-gray-400">
                {userRoleFilter === 'all' ? 'No active users found.' : `No users with role "${userRoleFilter}" found.`}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[#0CB4E4]/20">
                      <th className="py-2 px-3 font-semibold text-gray-300">NAME</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">EMAIL</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">ROLE</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">CREATED AT</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user._id || user.userId} className="border-b border-[#0CB4E4]/10">
                        <td className="py-2 px-3 text-white">{user.name || user.email || user.userId}</td>
                        <td className="py-2 px-3 text-white">{user.email}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.role === 'admin' ? 'bg-purple-900/30 text-purple-300 border border-purple-500/30' :
                            user.role === 'creator' ? 'bg-[#0CB4E4]/20 text-[#0CB4E4] border border-[#0CB4E4]/30' :
                            user.role === 'viewer' ? 'bg-green-900/30 text-green-400 border border-green-500/30' :
                            'bg-[#161513] text-gray-300 border border-gray-500/30'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-gray-300">{formatDate(user.createdAt)}</td>
                        <td className="py-2 px-3">
                          <button
                            className="text-[#0CB4E4] hover:underline text-sm"
                            onClick={() => openEditUserModal(user)}
                          >
                            Edit Role
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Waitlist Sign-Ups Table */}
          <div className="bg-[#161513] rounded-xl shadow-lg p-6 mb-8 border border-[#0CB4E4]/30">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Waitlist Sign-Ups</h2>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-300">Filter by role:</label>
                  <select
                    value={inviteRoleFilter}
                    onChange={(e) => setInviteRoleFilter(e.target.value)}
                    className="px-3 py-1 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] bg-[#161513] border border-[#0CB4E4]/30 text-white"
                  >
                    <option value="all">All Roles</option>
                    {ROLE_OPTIONS.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>
                <button
                  className="px-4 py-2 bg-[#0CB4E4] text-[#161513] rounded-lg font-medium hover:bg-[#0CB4E4]/90 transition-colors shadow-md"
                  onClick={openAddInviteModal}
                >
                  + Send Invitation
                </button>
              </div>
            </div>
            
            {loading ? (
              <div className="py-6 text-center text-gray-300">Loading...</div>
            ) : error ? null : invitedUsers.length === 0 ? (
              <div className="py-6 text-center text-gray-400">
                {inviteRoleFilter === 'all' ? 'No pending invitations found.' : `No "${inviteRoleFilter}" invitations found.`}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[#0CB4E4]/20">
                      <th className="py-2 px-3 font-semibold text-gray-300">NAME</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">EMAIL</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">ACCOUNT TYPE</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">STUDIO</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">PHONE</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">PROJECT</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">JOB TITLE</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">ROLE</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">INVITED BY</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">INVITED AT</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">STATUS</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitedUsers.map(entry => {
                      const profile = waitlistProfilesByEmail[entry.email];
                      const accountType = profile?.studioName === 'Personal Use' ? 'personal' : (profile ? 'work' : '-');
                      const displayStudio = profile?.studioName === 'Personal Use' ? '-' : (profile?.studioName || '-');
                      const jobTitle = (profile as any)?.jobTitle || '-';
                      return (
                      <tr key={entry.email} className="border-b border-[#0CB4E4]/10">
                        <td className="py-2 px-3 text-white">{profile?.fullName || '-'}</td>
                        <td className="py-2 px-3 text-white">{entry.email}</td>
                        <td className="py-2 px-3 text-white">{accountType}</td>
                        <td className="py-2 px-3 text-white">{displayStudio}</td>
                        <td className="py-2 px-3 text-white">{profile?.phoneNumber || '-'}</td>
                        <td className="py-2 px-3 text-white">{profile?.project || '-'}</td>
                        <td className="py-2 px-3 text-white">{jobTitle}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            entry.role === 'admin' ? 'bg-purple-900/30 text-purple-300 border border-purple-500/30' :
                            entry.role === 'creator' ? 'bg-[#0CB4E4]/20 text-[#0CB4E4] border border-[#0CB4E4]/30' :
                            entry.role === 'viewer' ? 'bg-green-900/30 text-green-400 border border-green-500/30' :
                            'bg-[#161513] text-gray-300 border border-gray-500/30'
                          }`}>
                            {entry.role}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-gray-300">{entry.addedBy}</td>
                        <td className="py-2 px-3 text-gray-300">{formatDate(entry.addedAt)}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            entry.invitationSent 
                              ? 'bg-[#0CB4E4]/20 text-[#0CB4E4] border border-[#0CB4E4]/30' 
                              : 'bg-[#0CB4E4]/10 text-[#0CB4E4] border border-[#0CB4E4]/30'
                          }`}>
                            {entry.invitationSent ? 'Sent' : 'Pending'}
                          </span>
                        </td>
                        <td className="py-2 px-3 flex gap-2">
                          <button
                            className="text-[#0CB4E4] hover:underline text-sm"
                            onClick={() => openEditInviteModal(entry)}
                          >
                            Edit
                          </button>
                          <button
                            className="text-red-400 hover:underline text-sm"
                            onClick={() => setDeleteTarget(entry)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Direct Sign-Ups Table */}
          <div className="bg-[#161513] rounded-xl shadow-lg p-6 mb-8 border border-[#0CB4E4]/30">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Direct Sign-Ups</h2>
            </div>
            {loading ? (
              <div className="py-6 text-center text-gray-300">Loading...</div>
            ) : error ? null : directProfiles.length === 0 ? (
              <div className="py-6 text-center text-gray-400">No direct sign-ups found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[#0CB4E4]/20">
                      <th className="py-2 px-3 font-semibold text-gray-300">NAME</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">EMAIL</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">ACCOUNT TYPE</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">STUDIO</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">PHONE</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">PROJECT</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">JOB TITLE</th>
                      <th className="py-2 px-3 font-semibold text-gray-300">ROLE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {directProfiles.map((p) => {
                      const accountType = p.studioName === 'Personal Use' ? 'personal' : (p.studioName ? 'work' : '-');
                      const displayStudio = p.studioName === 'Personal Use' ? '-' : (p.studioName || '-');
                      const roleForProfile =
                        (users.find(u => (u.userId === (p as any).userId) || (!!u.email && !!p.email && u.email === p.email))?.role)
                        || (whitelist.find(w => (!!w.userId && w.userId === (p as any).userId) || (w.email === p.email))?.role as any)
                        || '-';
                      return (
                        <tr key={p.userId} className="border-b border-[#0CB4E4]/10">
                          <td className="py-2 px-3 text-white">{p.fullName || '-'}</td>
                          <td className="py-2 px-3 text-white">{p.email || '-'}</td>
                          <td className="py-2 px-3 text-white">{accountType}</td>
                          <td className="py-2 px-3 text-white">{displayStudio}</td>
                          <td className="py-2 px-3 text-white">{p.phoneNumber || '-'}</td>
                          <td className="py-2 px-3 text-white">{p.project || '-'}</td>
                          <td className="py-2 px-3 text-white">{(p as any).jobTitle || '-'}</td>
                          <td className="py-2 px-3 text-white">{roleForProfile}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Add/Edit Modal */}
        {modal.open && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <form
              className="bg-[#161513] rounded-xl shadow-xl p-8 w-full max-w-md relative border border-[#0CB4E4]/30"
              onSubmit={handleModalSubmit}
            >
              <h3 className="text-xl font-semibold mb-4 text-white">
                {modal.mode === 'add' ? 'Send Invitation' : 
                 modal.mode === 'edit-user' ? 'Edit User Role' : 'Edit Invitation'}
              </h3>
              
              {modal.mode !== 'edit-user' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-white mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={(modal.entry as WhitelistEntry)?.email || ''}
                    onChange={handleModalInput}
                    className="w-full px-3 py-2 border rounded-lg bg-[#161513] border-[#0CB4E4]/30 text-white focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4]"
                    required
                    disabled={modal.mode === 'edit-invite'}
                    autoComplete="off"
                    autoFocus={modal.mode === 'add'}
                  />
                </div>
              )}
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-white mb-1">Role</label>
                <select
                  name="role"
                  value={modal.entry?.role || 'creator'}
                  onChange={handleModalInput}
                  className="w-full px-3 py-2 border rounded-lg bg-[#161513] border-[#0CB4E4]/30 text-white focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4]"
                  required
                >
                  {ROLE_OPTIONS.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-[#161513] text-gray-300 hover:text-white hover:bg-[#161513]/80 border border-[#0CB4E4]/30"
                  onClick={closeModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-[#0CB4E4] text-[#161513] font-medium hover:bg-[#0CB4E4]/90 disabled:opacity-60 shadow-md"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 
                   modal.mode === 'add' ? 'Send Invitation' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteTarget && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-[#161513] rounded-xl shadow-xl p-8 w-full max-w-md relative border border-[#0CB4E4]/30">
              <h3 className="text-xl font-semibold mb-4 text-white">Delete Invitation</h3>
              <p className="mb-6 text-gray-300">
                Are you sure you want to remove the invitation for{' '}
                <span className="font-bold text-white">{deleteTarget.email}</span>?
                <br />
                <span className="text-sm text-gray-400 mt-2 block">
                  This will permanently delete the invitation. The user will not be able to join.
                </span>
              </p>
              <div className="flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-lg bg-[#161513] text-gray-300 hover:text-white hover:bg-[#161513]/80 border border-[#0CB4E4]/30"
                  onClick={() => setDeleteTarget(null)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-red-500/70 text-white font-medium hover:bg-red-500/90 disabled:opacity-60 shadow-md"
                  onClick={handleDeleteInvite}
                  disabled={submitting}
                >
                  {submitting ? 'Deleting...' : 'Delete Invitation'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ProtectedRoute>
    </ErrorBoundary>
  );
}
